import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Camera, Search, MapPin, Star, Filter, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const PhotographerListing = () => {
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category');

  const photographers = [
    {
      id: 1,
      name: 'Sarah Johnson',
      specialty: 'Wedding Photography',
      location: 'New York, NY',
      rating: 4.9,
      reviews: 127,
      startingPrice: 2500,
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop',
      portfolio: 'https://images.unsplash.com/photo-1606800052052-a08af7148866?w=800&h=600&fit=crop',
    },
    {
      id: 2,
      name: 'Michael Chen',
      specialty: 'Food & Product',
      location: 'Los Angeles, CA',
      rating: 4.8,
      reviews: 89,
      startingPrice: 1200,
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop',
      portfolio: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&h=600&fit=crop',
    },
    {
      id: 3,
      name: 'Emma Rodriguez',
      specialty: 'Fashion & Editorial',
      location: 'Miami, FL',
      rating: 5.0,
      reviews: 156,
      startingPrice: 3000,
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop',
      portfolio: 'https://images.unsplash.com/photo-1529139574466-a303027c1d8b?w=800&h=600&fit=crop',
    },
    {
      id: 4,
      name: 'David Kim',
      specialty: 'Corporate & Events',
      location: 'Chicago, IL',
      rating: 4.7,
      reviews: 94,
      startingPrice: 1800,
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop',
      portfolio: 'https://images.unsplash.com/photo-1511578314322-379afb476865?w=800&h=600&fit=crop',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-background sticky top-0 z-50">
        <div className="px-6 md:px-12 lg:px-24 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
              <Camera className="w-8 h-8" />
              <span className="font-playfair text-2xl font-bold">Pixnest</span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/auth">
                <Button variant="ghost" size="sm" data-testid="header-signin">Sign In</Button>
              </Link>
              <Link to="/client/dashboard">
                <Button size="sm" data-testid="header-dashboard">Dashboard</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-12 lg:px-24 py-12">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8" data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        {/* Search and Filters */}
        <div className="mb-12">
          <h1 className="font-playfair text-4xl md:text-5xl font-bold mb-6" data-testid="page-title">
            {category ? `${category.charAt(0).toUpperCase() + category.slice(1)} Photographers` : 'Find Your Perfect Photographer'}
          </h1>

          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search by name, specialty, or location..."
                className="pl-12"
                data-testid="search-input"
              />
            </div>
            <Button variant="outline" data-testid="filter-button">
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>

          {/* Quick Filters */}
          <div className="flex flex-wrap gap-2" data-testid="quick-filters">
            <Badge variant="outline" className="cursor-pointer hover:bg-accent" data-testid="filter-weddings">Weddings</Badge>
            <Badge variant="outline" className="cursor-pointer hover:bg-accent" data-testid="filter-food">Food</Badge>
            <Badge variant="outline" className="cursor-pointer hover:bg-accent" data-testid="filter-fashion">Fashion</Badge>
            <Badge variant="outline" className="cursor-pointer hover:bg-accent" data-testid="filter-corporate">Corporate</Badge>
            <Badge variant="outline" className="cursor-pointer hover:bg-accent" data-testid="filter-portrait">Portrait</Badge>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6 text-muted-foreground" data-testid="results-count">
          Showing {photographers.length} photographers
        </div>

        {/* Photographer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="photographer-grid">
          {photographers.map((photographer) => (
            <Card key={photographer.id} className="overflow-hidden hover:shadow-xl transition-shadow duration-300" data-testid={`photographer-card-${photographer.id}`}>
              <div className="grid md:grid-cols-5 gap-0">
                {/* Portfolio Image */}
                <div className="md:col-span-2 relative h-64 md:h-auto">
                  <img
                    src={photographer.portfolio}
                    alt={photographer.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-white text-foreground" data-testid={`photographer-specialty-${photographer.id}`}>
                      {photographer.specialty}
                    </Badge>
                  </div>
                </div>

                {/* Info */}
                <CardContent className="md:col-span-3 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <img
                        src={photographer.image}
                        alt={photographer.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div>
                        <h3 className="font-playfair text-xl font-semibold" data-testid={`photographer-name-${photographer.id}`}>
                          {photographer.name}
                        </h3>
                        <div className="flex items-center text-sm text-muted-foreground">
                          <MapPin className="w-3 h-3 mr-1" />
                          <span data-testid={`photographer-location-${photographer.id}`}>{photographer.location}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center mb-4">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400 mr-1" />
                    <span className="font-semibold mr-1" data-testid={`photographer-rating-${photographer.id}`}>{photographer.rating}</span>
                    <span className="text-sm text-muted-foreground" data-testid={`photographer-reviews-${photographer.id}`}>
                      ({photographer.reviews} reviews)
                    </span>
                  </div>

                  <div className="flex items-center justify-between mt-6">
                    <div>
                      <div className="text-sm text-muted-foreground">Starting at</div>
                      <div className="font-playfair text-2xl font-bold" data-testid={`photographer-price-${photographer.id}`}>
                        ${photographer.startingPrice.toLocaleString()}
                      </div>
                    </div>
                    <Link to={`/photographer/${photographer.id}`}>
                      <Button data-testid={`view-profile-button-${photographer.id}`}>View Profile</Button>
                    </Link>
                  </div>
                </CardContent>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PhotographerListing;